import { MergeOrUpdateStateAction } from '@tableau/vizportalclient-common-components';
import React from 'react';

import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';
import { Messages } from '~src/codegen/Localize';
import { CheckboxState } from '~src/commonInfra/components/checkbox/CheckboxState';
import { openReactActionDialog } from '~src/commonInfra/dialogs/ReactActionDialog';
import { TabDialogWidth } from '~src/commonInfra/dialogs/style/TabDialogWidth';
import { ListResult } from '~src/httpClient/fetchers/ListResult';
import { SliceableHelper } from '~src/httpClient/utils/SliceableHelper';
import { ISiteSettingsForServerAdmin } from '~src/settings/types/ISite';
import { ISiteNameWithId } from '~src/siteManagement/serverApi/types/ISiteNameWithId';
import { Sites } from '~src/siteManagement/services/resources/Sites';

import {
  AdminProfileSiteManagementDialogBody,
  AdminProfileSiteManagementDialogProps,
  AdminProfileSiteManagementDialogState,
  hasSiteSelectionChanged,
} from '../components/AdminProfileSiteManagementDialog';
import { IAdminProfileSite } from '../models/IAdminProfile';
import { AdminProfilesService } from './AdminProfilesService';

function buildInitialDialogState(
  sitesWhereEnabled: ReadonlyArray<IAdminProfileSite>,
  result: ListResult<ISiteSettingsForServerAdmin>
): AdminProfileSiteManagementDialogState {
  const enabledSiteIds = new Set(sitesWhereEnabled.map((s) => s.id));
  const assignedCountBySiteId = new Map(
    sitesWhereEnabled.map((s) => [s.id, s.adminProfileAssignedUserCount])
  );
  const siteCheckboxState: Record<string, CheckboxState> = {};
  const assignedUsersBySite: Record<string, number> = {};
  const sites: ISiteNameWithId[] = result.items.map((site) => ({
    id: site.id,
    name: site.settings?.name,
    urlName: site.settings?.urlName ?? site.id,
    luid: site.id,
  }));

  for (const site of sites) {
    siteCheckboxState[site.id] = enabledSiteIds.has(site.id)
      ? CheckboxState.checked
      : CheckboxState.empty;
    assignedUsersBySite[site.id] = assignedCountBySiteId.get(site.id) ?? 0;
  }

  return {
    sites,
    siteCheckboxState,
    assignedUsersBySite,
    loading: false,
  };
}

export async function showAdminProfileSiteManagementDialog(
  profileId: string,
  profileName: string,
  sitesWhereEnabled: ReadonlyArray<IAdminProfileSite>,
  onProfileSitesUpdated?: () => void
): Promise<any> {
  const result = await SliceableHelper.fetchAll(Sites.instance.all());
  const initialDialogState = buildInitialDialogState(
    sitesWhereEnabled,
    result as ListResult<ISiteSettingsForServerAdmin>
  );

  const initialBodyProps: AdminProfileSiteManagementDialogProps = {
    profileId,
    profileName,
    sitesWhereEnabled,
    onProfileSitesUpdated,
  };

  const modalInstance = openReactActionDialog<
    AdminProfileSiteManagementDialogProps,
    AdminProfileSiteManagementDialogState,
    any
  >({
    title: Messages.adminProfileSiteManagementDialogTitle({ PROFILE_NAME: profileName }),
    bodyComponent: AdminProfileSiteManagementDialogBody,
    initialBodyProps,
    initialDialogState,
    actionName: Messages.save(),
    actionDisabled: (bodyProps, dialogState) => !hasSiteSelectionChanged(bodyProps, dialogState) || dialogState.loading,
    action: adminProfileSiteManagementAction(profileId, onProfileSitesUpdated),
    dismissDialogOnActionFailed: false,
    showCloseButton: true,
    dialogWidth: TabDialogWidth.width650px,
    dialogBodyStyle: { paddingTop: 0, paddingBottom: 24, paddingLeft: 24, paddingRight: 24 },
  });

  return modalInstance.result;
}

function adminProfileSiteManagementAction(
  profileId: string,
  onProfileSitesUpdated?: () => void
) {
  return async (
    _bodyProps: AdminProfileSiteManagementDialogProps,
    dialogState: AdminProfileSiteManagementDialogState,
    _updateDialogState: React.Dispatch<MergeOrUpdateStateAction<AdminProfileSiteManagementDialogState>>
  ): Promise<any> => {
    const siteIdsToEnable = dialogState.sites
      .filter((s) => dialogState.siteCheckboxState[s.id] === CheckboxState.checked)
      .map((s) => s.id);

    const result = await AdminProfilesService.updateAdminProfileSites({
      luid: profileId,
      siteIds: siteIdsToEnable,
    });

    if (!result.errors || result.errors.length === 0) {
      ToasterService.instance.pop({
        type: ToastType.Success,
        text: Messages.adminProfileSitesUpdatedSuccess(),
      });
      onProfileSitesUpdated?.();
      return result;
    }

    return Promise.reject(result);
  };
}

export const AdminProfileSiteManagementDialogAction = {
  showAdminProfileSiteManagementDialog,
};
